import { Component, Input } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { forkJoin, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { DiaryNote } from '@features/diary-notes/model/diary-note';
import {
  commandEvent,
  CommandEvent
} from '@shared/components/load/command/command.component';
import { DatesUtil } from '@shared/utilities/dates/dates.util';
import { diff } from 'deep-object-diff';
import { OpportunitySchemaUtil } from '@features/opportunities/modules/core/utils/opportunity-schema-util';
import { DiaryNoteService } from '@features/diary-notes/services/diary-note.service';

type LoadContent = {
  schema: DiaryNote.Definition;
  content: DiaryNote.Model;
};

@Component({ template: '' })
export abstract class OpportunityDiaryNoteDetailsComponent {
  @Input() cancel: () => void = () => {};
  @Input() saved: () => void = () => {};

  abstract get title(): string;
  abstract get saveLabel(): string;
  abstract get isNewDiaryNote(): boolean;
  abstract content(): Observable<DiaryNote.Model>;

  diaryNoteId!: string;
  load!: LoadContent;
  entityId!: string;

  private loadSchema$: Observable<DiaryNote.Definition> =
    this.oppDiaryNoteService.getDiaryNoteDefinition();

  load$!: Observable<LoadContent>;
  protected change$: Subject<any> = new Subject<any>();
  triggerValidation$ = new Subject<boolean>();

  update$: Observable<CommandEvent<object>> = commandEvent(
    this.change$,
    (x) => {
      const payload = this.isNewDiaryNote
        ? { ...x, createdDate: DatesUtil.localDateTime() }
        : x;
      return this.oppDiaryNoteService.updateDiaryNote(
        this.entityId,
        this.diaryNoteId,
        payload
      );
    }
  );

  form = this.fb.group({
    summary: [
      null as string | null,
      [Validators.required, Validators.maxLength(128)]
    ],
    details: [
      null as string | null,
      [Validators.required, Validators.maxLength(4000)]
    ],
    submit: [null as boolean | null],
    createdDate: [null as string | null]
  });

  constructor(
    protected oppDiaryNoteService: DiaryNoteService,
    protected fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.load$ = forkJoin([this.loadSchema$, this.content()]).pipe(
      map(([schema, content]) => ({ schema, content })),
      tap((x) => {
        OpportunitySchemaUtil.attachFormGroupToSchema(this.form, x.schema);
        this.form.patchValue(x.content);
        this.load = x;
      })
    );
  }

  save() {
    if (this.form.invalid) {
      this.triggerValidation$.next(true);
    }

    const formValue = this.form.getRawValue();
    let delta = diff(this.load.content, formValue) as Partial<DiaryNote.Model>;

    this.change$.next(delta);
  }
}
